import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import { navigate } from "@reach/router";
import { AuthContext } from "../helpers/AuthContext";
import Sidebar from "../components/Sidebar";
import "../styles/responsive.css";
import "../styles/results.css";

const AdminUserDetail = ({ email }) => {
  const context = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [classFilter, setClassFilter] = useState("all");
  const [agreementFilter, setAgreementFilter] = useState("all"); // all / agree / disagree
  const [expandedIC, setExpandedIC] = useState(null); // componentName currently expanded
  const [icDetails, setIcDetails] = useState({}); // { [componentName]: { loading, data, error } }

  // Editable weight state
  const [weightInput, setWeightInput] = useState("");
  const [savingWeight, setSavingWeight] = useState(false);
  const [weightStatus, setWeightStatus] = useState(null); // { ok, message }

  const decodedEmail = decodeURIComponent(email || "");

  useEffect(() => {
    if (context.Auth !== "Admin") return;
    if (!decodedEmail) return;
    setLoading(true);
    setError("");
    axios
      .get(`/api/admin/user/${encodeURIComponent(decodedEmail)}`, {
        headers: context.authHeader ? context.authHeader() : {},
      })
      .then((res) => {
        setData(res.data);
        // Initialize the editable weight input from server data
        const w = res.data?.user?.weight;
        if (typeof w === "number") setWeightInput(w.toFixed(2));
      })
      .catch((err) => {
        setError(err?.response?.data?.message || err.message || "Request failed");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.Auth, decodedEmail]);

  // Lazy-fetch component breakdown when a row is expanded for the first time
  useEffect(() => {
    if (!expandedIC) return;
    if (icDetails[expandedIC]) return; // already cached
    setIcDetails((prev) => ({
      ...prev,
      [expandedIC]: { loading: true, data: null, error: null },
    }));
    axios
      .get(`/api/admin/component/${encodeURIComponent(expandedIC)}`, {
        headers: context.authHeader ? context.authHeader() : {},
      })
      .then((res) => {
        setIcDetails((prev) => ({
          ...prev,
          [expandedIC]: { loading: false, data: res.data, error: null },
        }));
      })
      .catch((err) => {
        setIcDetails((prev) => ({
          ...prev,
          [expandedIC]: {
            loading: false,
            data: null,
            error:
              err?.response?.data?.message ||
              err.message ||
              "Failed to load breakdown",
          },
        }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedIC]);

  const handleSaveWeight = () => {
    const w = parseFloat(weightInput);
    if (!Number.isFinite(w) || w < 0 || w > 10) {
      setWeightStatus({ ok: false, message: "Weight must be 0–10" });
      return;
    }
    setSavingWeight(true);
    setWeightStatus(null);
    axios
      .post(
        "/api/admin/set-weight",
        { email: decodedEmail, weight: w },
        { headers: context.authHeader ? context.authHeader() : {} }
      )
      .then((res) => {
        setWeightStatus({ ok: true, message: "✓ Saved" });
        setData((prev) =>
          prev
            ? { ...prev, user: { ...prev.user, weight: res.data.weight } }
            : prev
        );
        setWeightInput(res.data.weight.toFixed(2));
        setTimeout(() => setWeightStatus(null), 2200);
      })
      .catch((err) => {
        setWeightStatus({
          ok: false,
          message:
            err?.response?.data?.message || err.message || "Save failed",
        });
      })
      .finally(() => setSavingWeight(false));
  };

  const filteredLabels = useMemo(() => {
    if (!data || !data.labels) return [];
    return data.labels.filter((l) => {
      if (classFilter !== "all") {
        const tags = Array.isArray(l.tags) ? l.tags : [];
        if (!tags.includes(classFilter)) return false;
      }
      const state =
        l.agreementState || (l.isAgreement ? "agree" : "disagree");
      if (agreementFilter === "agree" && state !== "agree") return false;
      if (agreementFilter === "partial" && state !== "partial") return false;
      if (agreementFilter === "disagree" && state !== "disagree") return false;
      return true;
    });
  }, [data, classFilter, agreementFilter]);

  if (context.Auth !== "Admin") {
    return (
      <>
        <Sidebar />
        <div className="page-content">
          <div
            style={{
              maxWidth: 560,
              margin: "100px auto",
              padding: "30px 36px",
              border: "1px solid rgba(8, 145, 178, 0.28)",
              borderRadius: 16,
              background: "#fff",
              textAlign: "center",
            }}
          >
            <h2 style={{ fontFamily: "Poppins, sans-serif", color: "#0a0e1a" }}>
              Admin only
            </h2>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Sidebar />
        <div className="page-content">
          <div style={{ padding: 100, textAlign: "center", color: "#4b5563" }}>
            Loading labeler profile…
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Sidebar />
        <div className="page-content">
          <div
            style={{
              maxWidth: 560,
              margin: "100px auto",
              padding: "30px 36px",
              border: "1px solid rgba(220, 38, 38, 0.3)",
              background: "rgba(220, 38, 38, 0.08)",
              color: "#dc2626",
              borderRadius: 12,
            }}
          >
            {error}
          </div>
        </div>
      </>
    );
  }

  if (!data) return null;
  const { user, labels, labelClasses } = data;
  const agreementColor =
    user.agreementPct === null
      ? "#9ca3af"
      : user.agreementPct >= 70
      ? "#059669"
      : user.agreementPct >= 40
      ? "#d97706"
      : "#dc2626";

  return (
    <>
      <Sidebar />
      <div className="page-content">
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "100px 36px 60px",
            fontFamily: "Poppins, sans-serif",
            color: "#0a0e1a",
          }}
        >
          {/* Back link */}
          <button
            onClick={() => navigate("/admin/quality")}
            style={{
              background: "transparent",
              border: "none",
              color: "#0891b2",
              fontFamily: "Poppins, sans-serif",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              cursor: "pointer",
              marginBottom: 18,
              padding: 0,
            }}
          >
            ← Back to overview
          </button>

          {/* Header card */}
          <div
            style={{
              padding: "30px 34px",
              border: "1px solid rgba(8, 145, 178, 0.22)",
              borderRadius: 18,
              background: "#fff",
              marginBottom: 20,
              boxShadow: "0 6px 24px rgba(15, 23, 42, 0.04)",
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#0891b2",
                marginBottom: 6,
              }}
            >
              ● Labeler // Profile
            </div>
            <h1
              style={{
                fontSize: 34,
                fontWeight: 700,
                margin: 0,
                color: "#0a0e1a",
              }}
            >
              {user.fname} {user.lname}
            </h1>
            <div
              style={{
                fontSize: 14,
                color: "#6b7280",
                marginTop: 4,
                marginBottom: 22,
              }}
            >
              {user.email} · {user.assoc || "—"}
              {!user.tutorialCompleted && (
                <span
                  style={{
                    marginLeft: 12,
                    padding: "2px 8px",
                    background: "rgba(217, 119, 6, 0.1)",
                    color: "#d97706",
                    borderRadius: 10,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  Tutorial not completed
                </span>
              )}
            </div>

            {/* Headline stats */}
            <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 18 }}>
              <StatCard
                label="Agreement"
                value={
                  user.agreementPct === null
                    ? "—"
                    : `${user.agreementPct.toFixed(1)}%`
                }
                color={agreementColor}
                subtitle={`on ${user.evaluated} ICs ≥ 3 labelers`}
                accent
              />
              <StatCard label="Total labels" value={user.totalLabels.toLocaleString()} />
              <StatCard label="Unique ICs" value={user.uniqueComponents} />

              {/* Editable weight */}
              <div
                style={{
                  flex: "0 0 auto",
                  minWidth: 200,
                  padding: "14px 20px",
                  border: "1px solid rgba(8, 145, 178, 0.22)",
                  background: "rgba(255,255,255,0.85)",
                  borderRadius: 12,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "#6b7280",
                    fontWeight: 600,
                    marginBottom: 6,
                  }}
                >
                  Weight (editable)
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <input
                    type="number"
                    step="0.05"
                    min="0"
                    max="10"
                    value={weightInput}
                    onChange={(e) => setWeightInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveWeight();
                    }}
                    style={{
                      width: 80,
                      padding: "8px 10px",
                      border: "1px solid rgba(8, 145, 178, 0.32)",
                      borderRadius: 8,
                      fontSize: 18,
                      fontWeight: 700,
                      fontFamily:
                        '"JetBrains Mono", "SF Mono", Menlo, monospace',
                      color: "#0a0e1a",
                      background: "#fff",
                      outline: "none",
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleSaveWeight}
                    disabled={savingWeight}
                    style={{
                      background: "#0891b2",
                      color: "white",
                      border: "1px solid #0891b2",
                      padding: "8px 14px",
                      borderRadius: 8,
                      fontFamily: "Poppins, sans-serif",
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      cursor: savingWeight ? "not-allowed" : "pointer",
                      opacity: savingWeight ? 0.65 : 1,
                    }}
                  >
                    {savingWeight ? "…" : "Save"}
                  </button>
                </div>
                {weightStatus && (
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 11,
                      color: weightStatus.ok ? "#059669" : "#dc2626",
                      fontWeight: 500,
                    }}
                  >
                    {weightStatus.message}
                  </div>
                )}
              </div>
            </div>

            {/* Expertise chips */}
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                paddingTop: 18,
                borderTop: "1px dashed rgba(8, 145, 178, 0.16)",
              }}
            >
              <Chip label="Degree" value={user.expertise.edu} />
              <Chip label="Labeling exp" value={user.expertise.exp} />
              <Chip label="EEG" value={user.expertise.expEEG} />
              <Chip label="MoBI" value={user.expertise.expMoBI} />
              <Chip label="ICA" value={user.expertise.expICA} />
              <Chip label="Cleans data" value={user.expertise.cleanFreq} />
            </div>
          </div>

          {/* Section: Most labeled ICs */}
          <SectionHeader
            eyebrow="Section 01 // Most labeled components"
            title="ICs sorted by total labelers"
            body="Sorted descending by how many different users labeled each IC. The ICs at the top are where disagreement matters most — consensus is high-signal."
          />

          {/* Filters */}
          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              marginBottom: 14,
              padding: "12px 16px",
              border: "1px solid rgba(8, 145, 178, 0.16)",
              borderRadius: 10,
              background: "rgba(255, 255, 255, 0.8)",
              flexWrap: "wrap",
            }}
          >
            <FilterPill
              label="Class"
              value={classFilter}
              options={[
                { value: "all", label: "All classes" },
                ...labelClasses.map((c) => ({ value: c, label: c })),
              ]}
              onChange={setClassFilter}
            />
            <FilterPill
              label="Agreement"
              value={agreementFilter}
              options={[
                { value: "all", label: "All" },
                { value: "agree", label: "Fully agrees" },
                { value: "partial", label: "Partial (some outliers)" },
                { value: "disagree", label: "Disagrees" },
              ]}
              onChange={setAgreementFilter}
            />
            <div style={{ flex: 1 }} />
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Showing {filteredLabels.length} of {labels.length}
            </div>
          </div>

          {/* Label table */}
          <div
            style={{
              background: "#fff",
              border: "1px solid rgba(8, 145, 178, 0.18)",
              borderRadius: 14,
              overflow: "hidden",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr
                  style={{
                    background: "rgba(8, 145, 178, 0.04)",
                    borderBottom: "1px solid rgba(8, 145, 178, 0.18)",
                  }}
                >
                  <Th>Component</Th>
                  <Th>User tags</Th>
                  <Th>Consensus tags</Th>
                  <Th align="right">Total labelers</Th>
                  <Th align="center">Agreement</Th>
                </tr>
              </thead>
              <tbody>
                {filteredLabels.map((l) => {
                  const isExpanded = expandedIC === l.componentName;
                  return (
                    <React.Fragment key={l.componentName}>
                      <tr
                        onClick={() =>
                          setExpandedIC(isExpanded ? null : l.componentName)
                        }
                        style={{
                          borderBottom: isExpanded
                            ? "none"
                            : "1px solid rgba(8, 145, 178, 0.08)",
                          cursor: "pointer",
                          background: isExpanded
                            ? "rgba(8, 145, 178, 0.04)"
                            : "transparent",
                          transition: "background 0.2s ease",
                        }}
                      >
                        <td style={tdStyle}>
                          <span
                            style={{
                              fontFamily: "monospace",
                              fontSize: 13,
                              color: "#4b5563",
                            }}
                          >
                            {isExpanded ? "▾ " : "▸ "}
                            {l.componentName}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <TagList tags={l.tags} color="#0891b2" />
                        </td>
                        <td style={tdStyle}>
                          <TagList tags={l.consensusTags} color="#6b7280" />
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>
                          {l.totalLabelersOnThisIC}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          {(() => {
                            if (l.totalLabelersOnThisIC < 3) {
                              return (
                                <span style={{ color: "#9ca3af", fontSize: 13 }}>
                                  —
                                </span>
                              );
                            }
                            // Prefer the new agreementState field; fall back to
                            // the old isAgreement boolean for safety.
                            const state =
                              l.agreementState ||
                              (l.isAgreement ? "agree" : "disagree");
                            const styles = {
                              agree: {
                                bg: "rgba(5, 150, 105, 0.1)",
                                color: "#059669",
                                label: "✓ Agrees",
                              },
                              partial: {
                                bg: "rgba(217, 119, 6, 0.1)",
                                color: "#d97706",
                                label: "◐ Partial",
                              },
                              disagree: {
                                bg: "rgba(220, 38, 38, 0.1)",
                                color: "#dc2626",
                                label: "✗ Disagrees",
                              },
                            }[state];
                            return (
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "4px 10px",
                                  background: styles.bg,
                                  color: styles.color,
                                  borderRadius: 10,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  letterSpacing: "0.06em",
                                  textTransform: "uppercase",
                                }}
                              >
                                {styles.label}
                              </span>
                            );
                          })()}
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr
                          style={{
                            borderBottom: "1px solid rgba(8, 145, 178, 0.08)",
                            background: "rgba(8, 145, 178, 0.025)",
                          }}
                        >
                          <td colSpan={5} style={{ padding: "20px 24px 26px" }}>
                            <div
                              style={{
                                display: "flex",
                                gap: 28,
                                alignItems: "flex-start",
                                flexWrap: "wrap",
                              }}
                            >
                              {/* IC image — bigger */}
                              <div
                                style={{
                                  flex: "1 1 600px",
                                  maxWidth: 760,
                                  background: "#fff",
                                  border: "1px solid rgba(8, 145, 178, 0.2)",
                                  borderRadius: 12,
                                  padding: 14,
                                  boxShadow:
                                    "0 4px 14px rgba(15, 23, 42, 0.06)",
                                }}
                              >
                                <div
                                  style={{
                                    fontFamily: "Poppins, sans-serif",
                                    fontSize: 10,
                                    fontWeight: 600,
                                    letterSpacing: "0.14em",
                                    textTransform: "uppercase",
                                    color: "#6b7280",
                                    marginBottom: 10,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <span>IC Image · {l.componentName}</span>
                                  <a
                                    href={`/dropbox/imagedata?imagefile=${encodeURIComponent(
                                      l.componentName
                                    )}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "#0891b2",
                                      textDecoration: "none",
                                      fontSize: 10,
                                    }}
                                  >
                                    Open full size ↗
                                  </a>
                                </div>
                                <img
                                  src={`/dropbox/imagedata?imagefile=${encodeURIComponent(
                                    l.componentName
                                  )}`}
                                  alt={l.componentName}
                                  style={{
                                    display: "block",
                                    width: "100%",
                                    height: "auto",
                                    borderRadius: 6,
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    e.currentTarget.parentElement.insertAdjacentHTML(
                                      "beforeend",
                                      '<div style="font-family: Poppins, sans-serif; font-size: 13px; color: #dc2626; padding: 12px;">Image failed to load from Dropbox.</div>'
                                    );
                                  }}
                                />
                              </div>

                              {/* Class breakdown — replaces consensus tags */}
                              <ClassBreakdownCard
                                componentName={l.componentName}
                                userTags={l.tags}
                                userEmail={user.email}
                                fallbackTotal={l.totalLabelersOnThisIC}
                                labelClasses={labelClasses}
                                state={icDetails[l.componentName]}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {filteredLabels.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      style={{ padding: 30, textAlign: "center", color: "#6b7280" }}
                    >
                      No labels match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

// ---------------- helpers ----------------

const tdStyle = {
  padding: "14px 16px",
  fontSize: 14,
  verticalAlign: "middle",
};

const ClassBreakdownCard = ({
  componentName,
  userTags,
  userEmail,
  fallbackTotal,
  labelClasses,
  state,
}) => {
  const cardWrap = {
    flex: "1 1 320px",
    minWidth: 280,
    background: "#fff",
    border: "1px solid rgba(8, 145, 178, 0.2)",
    borderRadius: 12,
    padding: "16px 20px 18px",
    boxShadow: "0 4px 14px rgba(15, 23, 42, 0.06)",
  };
  const headerStyle = {
    fontFamily: "Poppins, sans-serif",
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#0891b2",
    marginBottom: 12,
    display: "flex",
    alignItems: "center",
    gap: 6,
  };

  if (!state || state.loading) {
    return (
      <div style={cardWrap}>
        <div style={headerStyle}>● Class Breakdown</div>
        <div
          style={{
            fontSize: 13,
            color: "#6b7280",
            fontFamily: "Poppins, sans-serif",
          }}
        >
          Loading breakdown…
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div style={cardWrap}>
        <div style={headerStyle}>● Class Breakdown</div>
        <div style={{ fontSize: 13, color: "#dc2626" }}>{state.error}</div>
      </div>
    );
  }

  const data = state.data || {};
  const labels = Array.isArray(data.labels) ? data.labels : [];
  const totalLabelers = labels.length || fallbackTotal || 0;

  // Count per class
  const counts = labelClasses.map((cls) => {
    const pickers = labels.filter(
      (l) => Array.isArray(l.tags) && l.tags.includes(cls)
    );
    return {
      cls,
      count: pickers.length,
      pct: totalLabelers > 0 ? (pickers.length / totalLabelers) * 100 : 0,
      userPicked: Array.isArray(userTags) && userTags.includes(cls),
      onlyUser:
        pickers.length === 1 &&
        pickers[0].email &&
        pickers[0].email === userEmail,
      pickers: pickers.map((p) => p.email),
    };
  });

  // Only show classes with at least one vote — keeps the list short
  const visible = counts.filter((c) => c.count > 0);
  // Sort: user-picks first, then by count desc
  visible.sort((a, b) => {
    if (a.userPicked !== b.userPicked) return a.userPicked ? -1 : 1;
    return b.count - a.count;
  });

  return (
    <div style={cardWrap}>
      <div style={headerStyle}>
        <span>Class Breakdown</span>
        <span
          style={{
            color: "#6b7280",
            fontWeight: 500,
            letterSpacing: "0.06em",
          }}
        >
          · {totalLabelers} labelers
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {visible.length === 0 && (
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            No labels submitted on this IC yet.
          </div>
        )}
        {visible.map((row) => {
          const barColor = row.userPicked ? "#0891b2" : "#cbd5e1";
          return (
            <div
              key={row.cls}
              style={{
                position: "relative",
                padding: "10px 12px",
                background: row.userPicked
                  ? "rgba(8, 145, 178, 0.06)"
                  : "rgba(8, 145, 178, 0.02)",
                border: row.userPicked
                  ? "1px solid rgba(8, 145, 178, 0.32)"
                  : "1px solid rgba(8, 145, 178, 0.1)",
                borderLeft: row.userPicked
                  ? "3px solid #0891b2"
                  : "1px solid rgba(8, 145, 178, 0.1)",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <div
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: row.userPicked ? 700 : 500,
                    fontSize: 13,
                    color: row.userPicked ? "#0891b2" : "#0a0e1a",
                  }}
                >
                  {row.cls}
                  {row.userPicked && (
                    <span
                      style={{
                        marginLeft: 6,
                        fontSize: 9,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color: "#0891b2",
                        opacity: 0.85,
                      }}
                    >
                      this user
                    </span>
                  )}
                  {row.onlyUser && (
                    <span
                      style={{
                        marginLeft: 6,
                        padding: "2px 6px",
                        background: "rgba(220, 38, 38, 0.1)",
                        color: "#dc2626",
                        borderRadius: 8,
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                      }}
                    >
                      ⚠ outlier
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontFamily:
                      '"JetBrains Mono", "SF Mono", Menlo, monospace',
                    fontSize: 12,
                    color: "#0a0e1a",
                    fontWeight: 600,
                  }}
                >
                  {row.count}/{totalLabelers}{" "}
                  <span style={{ color: "#9ca3af", fontWeight: 500 }}>
                    ({row.pct.toFixed(0)}%)
                  </span>
                </div>
              </div>
              <div
                style={{
                  width: "100%",
                  height: 4,
                  background: "rgba(8, 145, 178, 0.08)",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${row.pct}%`,
                    height: "100%",
                    background: barColor,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Th = ({ children, align }) => (
  <th
    style={{
      padding: "12px 16px",
      textAlign: align || "left",
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: "#6b7280",
    }}
  >
    {children}
  </th>
);

const StatCard = ({ label, value, subtitle, color, accent }) => (
  <div
    style={{
      flex: "0 0 auto",
      minWidth: 160,
      padding: "14px 20px",
      border: accent ? `1px solid ${color || "rgba(8, 145, 178, 0.3)"}` : "1px solid rgba(8, 145, 178, 0.2)",
      background: accent ? "rgba(8, 145, 178, 0.04)" : "rgba(255,255,255,0.8)",
      borderRadius: 12,
    }}
  >
    <div
      style={{
        fontSize: 10,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: "#6b7280",
        fontWeight: 600,
        marginBottom: 4,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 26,
        fontWeight: 700,
        color: color || "#0a0e1a",
        lineHeight: 1,
      }}
    >
      {value}
    </div>
    {subtitle && (
      <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>{subtitle}</div>
    )}
  </div>
);

const Chip = ({ label, value }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "5px 12px",
      background: "rgba(8, 145, 178, 0.06)",
      border: "1px solid rgba(8, 145, 178, 0.18)",
      borderRadius: 14,
      fontSize: 12,
    }}
  >
    <span
      style={{
        fontWeight: 600,
        color: "#6b7280",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        fontSize: 10,
      }}
    >
      {label}
    </span>
    <span style={{ color: "#0a0e1a", fontWeight: 500 }}>{value || "—"}</span>
  </div>
);

const SectionHeader = ({ eyebrow, title, body }) => (
  <div style={{ marginTop: 28, marginBottom: 14 }}>
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: "#0891b2",
        marginBottom: 6,
      }}
    >
      ● {eyebrow}
    </div>
    <h2
      style={{
        fontSize: 24,
        fontWeight: 700,
        margin: 0,
        color: "#0a0e1a",
      }}
    >
      {title}
    </h2>
    {body && (
      <p
        style={{
          fontSize: 13,
          color: "#6b7280",
          maxWidth: 620,
          marginTop: 4,
        }}
      >
        {body}
      </p>
    )}
  </div>
);

const FilterPill = ({ label, value, options, onChange }) => (
  <label
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11,
      color: "#6b7280",
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    }}
  >
    {label}:
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        padding: "6px 10px",
        border: "1px solid rgba(8, 145, 178, 0.24)",
        borderRadius: 6,
        fontSize: 13,
        background: "#fff",
        color: "#0a0e1a",
        fontFamily: "Poppins, sans-serif",
        fontWeight: 500,
        letterSpacing: 0,
        textTransform: "none",
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </label>
);

const TagList = ({ tags, color }) => {
  if (!tags || tags.length === 0)
    return <span style={{ color: "#9ca3af", fontSize: 13 }}>—</span>;
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {tags.map((t) => (
        <span
          key={t}
          style={{
            padding: "3px 8px",
            background: "rgba(8, 145, 178, 0.06)",
            border: `1px solid ${color}22`,
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            color,
            letterSpacing: "0.02em",
          }}
        >
          {t}
        </span>
      ))}
    </div>
  );
};

export default AdminUserDetail;
